import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Typography,
  Paper,
  useTheme,
  useMediaQuery,
  Stack,
  Rating,
} from '@mui/material';
import { Chrome } from 'lucide-mui';
import { useLocation } from 'react-router-dom';
import { useIframeResizer } from './common';

const AddToChrome: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  useIframeResizer();

  const handleAddToChrome = () => {
    // Get the intent parameter from the URL if it exists
    const hashPart = location.hash.split('?')[1] || '';
    const urlParams = new URLSearchParams(hashPart);
    const intent = urlParams.get('intent');

    // Navigate to the external URL with the intent parameter only if it exists
    if (intent) {
      window.open(`https://screenapp.io/chrome?intent=${intent}`, '_blank');
    } else {
      window.open('https://screenapp.io/chrome', '_blank');
    }
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
      <Box sx={{ p: 3, maxWidth: '60rem', width: '100%' }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 4 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            borderRadius: { xs: '8px', sm: '12px' },
            boxShadow: 'none',
            transition: 'all 0.2s ease-in-out',
            boxSizing: 'border-box',
            overflow: 'hidden',
            '&:hover': {
              transform: 'translateY(-2px)',
            },
          }}
        >
          <Chrome
            sx={{
              fontSize: { xs: 40, sm: 56 },
              color: 'primary.main',
              transition: 'all 0.2s ease-in-out',
              '@keyframes floatAnimation': {
                '0%': { transform: 'scale(1)' },
                '50%': { transform: 'scale(1) translateY(-5px)' },
                '100%': { transform: 'scale(1)' },
              },
              '.MuiPaper-root:hover &': {
                animation: 'floatAnimation 2s ease-in-out infinite',
              },
            }}
          />
          <Typography
            variant={isMobile ? 'h6' : 'h5'}
            sx={{
              mt: 2,
              mb: 1,
              textAlign: 'center',
              px: { xs: 0.5, sm: 2 },
              wordBreak: 'break-word',
            }}
          >
            {t('tools.addtochrome.title')}
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{
              mb: 3,
              textAlign: 'center',
              px: { xs: 0.5, sm: 2 },
              fontSize: { xs: '0.875rem', sm: '1rem' },
              wordBreak: 'break-word',
            }}
          >
            {t('tools.addtochrome.subtitle')}
          </Typography>
          <Button
            variant="contained"
            size={isMobile ? 'medium' : 'large'}
            startIcon={<Chrome />}
            onClick={handleAddToChrome}
            fullWidth={isMobile}
            sx={{
              px: { xs: 1.5, sm: 4 },
              py: { xs: 1, sm: 1.5 },
              textTransform: 'none',
              fontSize: { xs: '0.875rem', sm: '1rem' },
              boxShadow: 'none',
              whiteSpace: { xs: 'normal', sm: 'nowrap' },
              minWidth: { xs: '100%', sm: 'auto' },
              position: 'relative',
            }}
          >
            <Stack direction="column" alignItems="center" spacing={0}>
              <Typography component="span" sx={{ lineHeight: 1.2 }}>
                {t('tools.addtochrome.button')}
              </Typography>
              <Typography
                component="span"
                sx={{
                  fontSize: '0.7rem',
                  opacity: 0.7,
                  lineHeight: 1,
                  mt: '2px',
                  fontWeight: 'normal',
                }}
              >
                It&apos;s free!
              </Typography>
            </Stack>
          </Button>

          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            sx={{
              mt: 2,
              opacity: 0.9,
              backgroundColor: 'rgba(0, 0, 0, 0.03)',
              borderRadius: '16px',
              px: 1.5,
              py: 0.5,
            }}
          >
            <Rating
              value={4.7}
              precision={0.1}
              readOnly
              size="small"
              sx={{
                '& .MuiRating-iconFilled': {
                  color: theme.palette.mode === 'dark' ? '#FFD700' : '#FF9800',
                },
                '& .MuiRating-iconEmpty': {
                  color:
                    theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.3)'
                      : 'rgba(0, 0, 0, 0.1)',
                },
              }}
            />
            <Typography
              variant="caption"
              sx={{
                fontWeight: 500,
                fontSize: '0.75rem',
                ml: 0.5,
                color: theme.palette.text.secondary,
              }}
            >
              4.7
            </Typography>
          </Stack>
        </Paper>
      </Box>
    </Box>
  );
};

export default AddToChrome;
